import React from "react";
import { Card } from "@/components/ui/card";
import { CircleCheck, CircleAlert, CircleX } from "lucide-react";
import { cn } from "@/lib/utils";

const EDITIONS = ["express", "professional", "expert", "corporate"];
const EDITION_LABELS = {
  express: "Express+",
  professional: "Professional+",
  expert: "Expert",
  corporate: "Corporate",
};

function StatusCell({ status, value }) {
  if (value) {
    return <span className="text-sm text-foreground">{value}</span>;
  }
  if (status === "included") return <CircleCheck className="w-4 h-4 text-emerald-500 mx-auto" />;
  if (status === "optional") return <CircleAlert className="w-4 h-4 text-amber-500 mx-auto" />;
  return <CircleX className="w-4 h-4 text-rose-500 mx-auto" />;
}

export default function ComparisonTable({ features, loading }) {
  if (loading || !features) {
    return <div className="p-12 text-center text-muted-foreground">Lädt …</div>;
  }

  return (
    <Card className="p-5 md:p-6 border-border bg-card card-elev">
      <div className="mb-4">
        <h2 className="text-xl font-semibold">Vergleichstabelle</h2>
        <p className="text-sm text-muted-foreground">
          Direkter Vergleich aller vier Editionen anhand der offiziellen Milestone-Merkmale.
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <CircleCheck className="w-4 h-4 text-emerald-500" /> Enthalten
        </span>
        <span className="inline-flex items-center gap-1">
          <CircleAlert className="w-4 h-4 text-amber-500" /> Optionale Lizenz
        </span>
        <span className="inline-flex items-center gap-1">
          <CircleX className="w-4 h-4 text-rose-500" /> Nicht verfügbar
        </span>
      </div>

      {/* Edition heads */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table data-testid="comparison-table" className="w-full text-sm">
          <thead>
            <tr className="bg-secondary/60 border-b border-border">
              <th className="text-left px-4 py-3 font-semibold w-[38%]">Merkmal</th>
              {EDITIONS.map((e) => (
                <th key={e} className={cn("text-center px-3 py-3 font-semibold", e === "corporate" && "text-primary")}>
                  <div>{EDITION_LABELS[e]}</div>
                  <div className="text-[10px] font-normal text-muted-foreground truncate max-w-[180px] mx-auto">
                    {features.editions[e]?.tagline}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.featureGroups.map((g) => (
              <React.Fragment key={g.key}>
                <tr>
                  <td
                    colSpan={5}
                    className="bg-secondary/30 border-t border-border px-4 py-2 text-xs uppercase tracking-wider text-primary font-semibold"
                  >
                    {g.label}
                  </td>
                </tr>
                {g.features.map((f) => (
                  <tr key={f.key} className="border-t border-border hover:bg-secondary/20">
                    <td className="px-4 py-2.5">{f.label}</td>
                    {EDITIONS.map((e) => {
                      const val = f.values[e];
                      const isStatus = f.type === "status";
                      return (
                        <td key={e} className="px-3 py-2.5 text-center">
                          {isStatus ? (
                            <StatusCell status={val} />
                          ) : (
                            <span className="text-sm text-foreground">{val || "—"}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-muted-foreground mt-3">
        * Abhängig von Systemkonfiguration. Quelle: Milestone Systems XProtect Vergleich.
      </p>
    </Card>
  );
}
